import _ from 'underscore';

import RetailManager from 'app/retail_manager/RetailManager';
import BasePaymentController from 'app/src/controllers/payments/BasePaymentController';
import PaymentListViewController from 'app/src/controllers/payments/PaymentListViewController';
import Decimal from 'app/src/data/base/Decimal';
import CreditAccount from 'app/src/data/models/CreditAccount';
import PaymentType from 'app/src/data/models/PaymentType';
import PaymentTypeCollection from 'app/src/data/models/PaymentTypeCollection';
import RetailCollection from 'app/src/data/models/RetailCollection';
import Localize from 'app/src/localize/Localize';
import DepositManager from 'app/src/payments/DepositManager';
import GiftCardManager from 'app/src/payments/GiftCardManager';
import PaymentFactory from 'app/src/payments/PaymentFactory';
import PaymentProvider from 'app/src/payments/PaymentProvider';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import SaleCalculationHelper from 'app/src/payments/SaleCalculationHelper';
import SaleManager from 'app/src/payments/SaleManager';
import PaymentDefaultView from 'app/src/views/payments/PaymentDefaultView';
import { toBoolean } from 'pages/utils/sanitize';
import { hasFeature } from 'root/api/admin/features';
import Modules from 'shared_modules/Modules';

export default class PaymentDefaultViewController extends BasePaymentController {
  constructor(saleManager, paymentProvider) {
    if (!(paymentProvider instanceof PaymentProvider)) {
      throw 'A payment provider must be provided.';
    }

    super(saleManager);

    this._paymentProvider = paymentProvider;

    let denominations = Localize.getLocalizedDenominations();
    let refundablePaymentTypes = null;
    let showMinimumRefund = true;

    this.refundOldTransactionID = null;
    this.refundOptions = {};

    if (this._saleManager.isRefundSale()) {
      this.refundOldTransactionID = $('#register').attr('refund_old_transaction_id');
      // Get the General Options refund options
      this.refundOptions = Localize.options.reduce((options, option) => {
        const optionName = option.get('name');

        const isRefundOption = [
          PaymentType.Refunds.CREDIT_ACCOUNT_OR_GIFTCARD_ONLY,
          PaymentType.Refunds.DISABLE_CREDIT_ACCOUNT_REFUNDS,
          PaymentType.Refunds.DISABLE_GIFTCARD_REFUNDS,
        ].includes(optionName);

        if (isRefundOption) {
          const optionValue = toBoolean(option.get('value'));
          if (
            optionValue &&
            (optionName !== PaymentType.Refunds.CREDIT_ACCOUNT_OR_GIFTCARD_ONLY ||
              (optionName === PaymentType.Refunds.CREDIT_ACCOUNT_OR_GIFTCARD_ONLY &&
                !this.refundOldTransactionID))
          ) {
            options[optionName] = optionValue;
          }
        }

        return options;
      }, {});

      // We don't want to display Gift Card / Credit Card refund types on this screen
      refundablePaymentTypes = saleManager.getRefundablePaymentTypes().filter(paymentType => {
        let type = paymentType.type;
        return type !== PaymentType.Types.CREDIT_CARD && type !== PaymentType.Types.GIFT_CARD;
      });

      // Don't show minimum refund there's only one available Payment Type
      // If we're not doing a refund sale, this variable is not used.
      if (this._saleManager.getRefundablePaymentTypes().length === 1) {
        showMinimumRefund = false;
      }
    }

    this.view = new PaymentDefaultView({
      viewData: {
        denominations: denominations.toJSON(),
        currencySymbol: Localize.getCurrencySymbol(),
        saleData: this._saleManager.toJSON(),
        userDefinedPaymentTypes: PaymentTypeManager.getUserDefinedPaymentTypes().toJSON(),
        refundablePaymentTypes,
        cashPaymentTypeID: PaymentTypeManager.getPaymentIdForType(PaymentTypeManager.Types.CASH),
        availableCreditBalance: saleManager.availableCreditBalance,
        accountPaymentTypeID: PaymentTypeManager.getPaymentIdForType(
          PaymentTypeManager.Types.CREDIT_ACCOUNT
        ),
        showRefundDisabledWarning: !!Object.keys(this.refundOptions).length,
        showRefundCreditAccountOrGiftCardOnlyWarning: !!this.refundOptions[
          PaymentType.Refunds.CREDIT_ACCOUNT_OR_GIFTCARD_ONLY
        ],
        showRefundCreditAccountDisabledWarning: !!this.refundOptions[
          PaymentType.Refunds.DISABLE_CREDIT_ACCOUNT_REFUNDS
        ],
        showRefundGiftCardDisabledWarning: !!this.refundOptions[
          PaymentType.Refunds.DISABLE_GIFTCARD_REFUNDS
        ],
        disableGiftCardButton: this._isPaymentTypeDisabledFromOptions({
          paymentType: PaymentType.Types.GIFT_CARD,
        }),
        // The following is disgusting but the reason why we doing this
        // is because we should not disable the credit/debit button
        // if we do not have a way of paying via credit/debit
        // but we are using Retail Manager
        disableCreditDebitButton:
          this._isPaymentTypeDisabledFromOptions({
            paymentType: PaymentType.Types.CREDIT_CARD,
          }) ||
          (!this._paymentProvider.activeGatewayHasConfiguredTerminals() &&
            !this._paymentProvider.activeGatewaySupportsUSBSwipe() &&
            !this._paymentProvider.activeGatewaySupportsManualEntry &&
            !this._paymentProvider.activeGatewaySupportsManualEntryFromTerminal &&
            !RetailManager.active) ||
          (this._paymentProvider.activeGatewaySupportsManualEntryFromTerminal &&
            !this._paymentProvider.activeGatewaySupportsManualEntry &&
            !this._paymentProvider.activeGatewayHasConfiguredTerminals() &&
            !this._paymentProvider.activeGatewaySupportsUSBSwipe()),
        showMinimumRefund,
      },
    });
  }

  /**
   * Event handler called before the view is rendered.
   */
  onBeforeRender() {
    window.Handlebars.registerHelper('allowCreditBalance', availableCreditBalance => {
      if (availableCreditBalance && availableCreditBalance.isGreaterThan(0)) {
        return new window.Handlebars.SafeString('');
      }

      return new window.Handlebars.SafeString('disabled');
    });
  }

  /**
   * Event handler called before the view is destroyed.
   */
  onBeforeDestroy() {
    window.Handlebars.unregisterHelper('allowCreditBalance');
  }

  /**
   * Initializes and displays the payment view.  Binds to sale manager events.
   */
  show() {
    super.show();

    this.attachChildViewController(
      new PaymentListViewController(this._saleManager, this._paymentProvider),
      'paymentList'
    );

    this.listenTo(
      this._saleManager,
      SaleManager.Events.CHANGE_DUE_CHANGED,
      this.onChangeDueChanged
    );

    // refresh the view when deposits have been added or removed
    this.listenTo(this._saleManager, SaleManager.Events.DEPOSITS_CHANGED, this.setInitialViewState);
    this.listenTo(
      this._saleManager.deposits,
      DepositManager.Events.DEPOSITS_CHANGED,
      this.setInitialViewState
    );
  }

  /**
   * Sets the initial state of dynamic data within the view.
   */
  setInitialViewState() {
    // show or hide the credit card and gift card buttons
    if (this._saleManager.isRefundSale()) {
      // Refundable payments that were originally paid for by Credit/Debit
      let refundableCreditPayments = this._getRefundableCreditPayments();

      // Payments that are refundable by Credit/Debit
      let paymentsRefundableByCredit = this._getPaymentsRefundableByCredit();

      // A combination of whether or not the gateway itself supports
      // negative payments and the status of the "Allow Credits" checkbox
      const gatewayAllowsNegativePayments = this._paymentProvider.activeGateway
        ? this._paymentProvider.activeGateway.allowNegativePayments
        : false;

      // Hide the credit/debit button if...
      // 1) The gateway does not support refunds
      // 2) There are no payments to refund and the gateway does not allow negative payments
      // 3) There are payments to refund, but none of them can be refunded
      //    as Credit/Debit
      // 4) There are payments to refund that can be refunded as Credit/Debit, none of which are
      //    made by reference, and the gateway does not allow negative payments.
      let hideCreditCardButton =
        !this._paymentProvider.activeGatewaySupportsRefunds ||
        (!this._saleManager.refundablePayments.length && !gatewayAllowsNegativePayments) ||
        (this._saleManager.refundablePayments.length && !paymentsRefundableByCredit.length) ||
        (paymentsRefundableByCredit.length &&
          !refundableCreditPayments.length &&
          !gatewayAllowsNegativePayments);

      // Edge case- there are Credit/Debit Payments not refundable by Credit/Debit Payments
      // on the same sale as payments that can only be refunded as credit/debit
      // while the gateway does not allow negative payments
      if (
        !hideCreditCardButton &&
        !gatewayAllowsNegativePayments &&
        refundableCreditPayments.length &&
        paymentsRefundableByCredit.length
      ) {
        // Check that all Credit/Debit Payments cannot be refunded as Credit/Debit
        let refundableCreditPaymentsRefundableByCredit = RetailCollection.intersectionByModelID(
          refundableCreditPayments,
          paymentsRefundableByCredit
        );

        // Check that all non-Credit/Debit Payments can ONLY be refunded as Credit/Debit
        let refundableNonCreditPayments = RetailCollection.differenceByModelID(
          this._saleManager.refundablePaymentList.toSalePaymentCollection(),
          refundableCreditPayments
        );
        let refundableNonCreditPaymentsRefundableByCredit = RetailCollection.intersectionByModelID(
          refundableNonCreditPayments,
          paymentsRefundableByCredit
        );

        // If both cases are true, hide the button
        if (
          !refundableCreditPaymentsRefundableByCredit.length &&
          refundableNonCreditPayments.length ===
            refundableNonCreditPaymentsRefundableByCredit.length
        ) {
          hideCreditCardButton = true;
        }
      }

      let canRefundGiftCard = !!PaymentTypeManager.getRefundablePaymentTypes(
        this._saleManager.refundablePayments
      ).findWhere({ type: PaymentType.Types.GIFT_CARD });

      // For the situation where payments can only be refunded as Credit/Debit,
      // but the Credit/Debit button has to be hidden for some reason. Instead
      // of displaying nothing, show the button but disable it so users don't
      // freak out
      if (
        this._paymentProvider.activeGateway &&
        this._canOnlyRefundAsCredit() &&
        hideCreditCardButton
      ) {
        this.view.showCreditDebitButton(true);
        this.view.disableCreditDebitButton();
      } else {
        this.view.showCreditDebitButton(
          this._paymentProvider.activeGateway !== null && !hideCreditCardButton
        );
      }

      this.view.showGiftCardButton(
        (GiftCardManager.paymentsEnabled(this._saleManager.giftCardModule) && canRefundGiftCard) ||
          (canRefundGiftCard && Modules.hasModule('omni.giftcards'))
      );
    } else {
      this.view.showCreditDebitButton(this._paymentProvider.activeGateway !== null);
      this.view.showGiftCardButton(
        GiftCardManager.paymentsEnabled(this._saleManager.giftCardModule) ||
          Modules.hasModule('omni.giftcards')
      );
    }

    // setup user defined payment amounts
    this._setUserDefinedPayments();

    // set cash payment amount
    this._setCashPayment();

    // set credit account payment amount
    this._setCreditAccountPayment();

    this._setViewChangeDue();

    // Focus the cursor on the cash field and highlight the default "$0.00"
    this.view.setFocusOnCashField();

    // Hide the cash balance by default
    this.view.showCashBalance(false);
  }

  /**
   * Event handler for when a cash denomination is clicked.
   *
   * @param denomination {string}  The value of the cash denomination to be added to the payments.
   */
  onDenominationClicked(denomination) {
    let cashDenomination;
    try {
      cashDenomination = new Decimal(denomination);
    } catch (err) {
      cashDenomination = new Decimal(NaN);
    }

    let payment = PaymentFactory.createPayment(PaymentTypeManager.Types.CASH, cashDenomination);
    let result = this._saleManager.validatePayment(payment);
    if (result.error === SaleManager.Errors.USER_FACING) {
      // TODO: replace this mechanism to remove the dependency on the legacy implementation
      alertUser(result.msg);
    } else if (!cashDenomination.isNaN()) {
      this._saleManager.addPayment(payment, { merge: true });
    }
    const cashPaymentTypeID = PaymentTypeManager.getPaymentIdForType(PaymentTypeManager.Types.CASH);
    let amount = this._saleManager.getTotalPaymentsOfType(cashPaymentTypeID);
    this._setViewPayment(cashPaymentTypeID, amount);
  }

  /**
   * Event handler for when the value of a payment field is modified.
   *
   * @param paymentTypeID {Integer}    The payment type ID of the payment type field that has changed
   * @param paymentAmount {Decimal}   The new amount
   */
  onPaymentChanged(paymentTypeID, paymentAmount) {
    let balance;
    let amount = new Decimal(NaN);
    let paymentTypeName = PaymentTypeManager.getPaymentTypeByAttributes({
      paymentTypeID,
    });

    try {
      // Determine how much balance would be left to pay if you exclude any
      // prior value of the payment that has changed.
      balance = this._getBalanceWithoutPaymentOfType(paymentTypeID);

      if (_.isEmpty(paymentAmount)) {
        amount = new Decimal(0);
      } else {
        amount = new Decimal(paymentAmount);
      }

      if (this._saleManager.isRefundSale()) {
        if (!amount.isNegative()) {
          // refund amounts must be negative
          amount = amount.negated();
        }

        let maxRefund = SaleCalculationHelper.calculateMaxRefundForPaymentType(
          this._saleManager,
          paymentTypeID
        );
        if (amount.isLessThan(maxRefund)) {
          amount = maxRefund;
        }
      } else {
        if (amount.isNegative()) {
          // payments must be positive
          amount = amount.negated();
        }

        if (paymentTypeName === PaymentTypeManager.Types.CREDIT_ACCOUNT) {
          let availableCredit = this._saleManager.availableCreditBalance;

          // amount should not exceed the available credit.
          if (availableCredit.isLessThan(amount)) {
            amount = availableCredit;
          }

          // there's no change,  amount should not exceed
          // a positive balance.
          if (amount.isGreaterThan(balance) && balance.isGreaterThan(new Decimal(0))) {
            // update the input to the correct balance.
            amount = balance;
            this._setViewPayment(paymentTypeID, balance);
          }
        }
      }
    } catch (err) {
      amount = new Decimal(NaN);
    }

    if (!amount.isNaN()) {
      let payment;
      switch (paymentTypeName) {
        case PaymentTypeManager.Types.USER_DEFINED:
        case PaymentTypeManager.Types.ECOM:
          payment = PaymentFactory.createPayment(paymentTypeName, amount, paymentTypeID);
          break;

        case PaymentTypeManager.Types.CREDIT_ACCOUNT:
          payment = PaymentFactory.createPayment(
            paymentTypeName,
            amount,
            this._saleManager.creditAccountID
          );
          break;

        default:
          payment = PaymentFactory.createPayment(paymentTypeName, amount);
      }

      let result = this._saleManager.validatePayment(payment);
      if (result.error === SaleManager.Errors.USER_FACING && !this._saleManager.isRefundSale()) {
        // TODO: replace this mechanism to remove the dependency on the legacy implementation
        alertUser(result.msg);

        // revert the payment field to the current value
        this._resetViewToPreviousAmount(paymentTypeID);
      } else {
        if (this._saleManager.hasExistingPaymentOfType(payment)) {
          this._saleManager.updatePaymentAmount(payment);
        } else {
          this._saleManager.addPayment(payment);
        }
        this._setViewPayment(paymentTypeID, amount);
      }
    } else {
      // revert the payment field to the current value
      this._resetViewToPreviousAmount(paymentTypeID);
    }
  }

  /**
   * Event handler for when cash 'Max' button is clicked
   */
  onMaxButtonClicked(paymentTypeID) {
    let amount = new Decimal(NaN);
    let balance;
    let payment;
    try {
      // ignore any prior contribution to the field so we can determine the maximum value
      // we can contribute using this payment type
      balance = this._getBalanceWithoutPaymentOfType(paymentTypeID);

      if (!balance.isZero() && !balance.isNaN()) {
        amount = balance;
        // If it's a refund sale, we might have to respect Refund Limits and
        // the amounts already paid
        if (this._saleManager.isRefundSale()) {
          amount = SaleCalculationHelper.calculateMaxRefundForPaymentType(
            this._saleManager,
            paymentTypeID
          );
        }
      }
    } catch (err) {
      amount = new Decimal(NaN);
    }

    if (!amount.isNaN() && !amount.isZero()) {
      let paymentType = PaymentTypeManager.getPaymentTypeByAttributes({
        paymentTypeID,
      });

      // Only continue with a negative amount if the sale is actually a refund.
      if (!amount.isNegative() || this._saleManager.isRefundSale()) {
        switch (paymentType) {
          case PaymentTypeManager.Types.USER_DEFINED:
          case PaymentTypeManager.Types.ECOM:
          case PaymentTypeManager.Types.ADJUSTMENT:
            payment = PaymentFactory.createPayment(paymentType, amount, paymentTypeID);
            break;

          case PaymentTypeManager.Types.CREDIT_ACCOUNT:
            let availableCredit = this._saleManager.availableCreditBalance;
            if (amount.isGreaterThan(availableCredit)) {
              amount = availableCredit;
            }
            payment = PaymentFactory.createPayment(
              paymentType,
              amount,
              this._saleManager.creditAccountID
            );
            break;

          default:
            payment = PaymentFactory.createPayment(paymentType, amount);
        }

        if (this._saleManager.hasExistingPaymentOfType(payment)) {
          this._saleManager.updatePaymentAmount(payment);
        } else {
          this._saleManager.addPayment(payment);
        }
        this._setViewPayment(paymentTypeID, amount);
      }
    }
  }

  /**
   * Event handler for when the sale manager change due has changed
   */
  onChangeDueChanged() {
    this._setViewChangeDue();
  }

  /**
   * Event Handler for when the user presses the Enter Key after submitting
   * a Cash Payment or User Defined Payment in any manner.
   * Tries to Complete the sale if the total Pending Payments are equal or greater than the Sale Total
   */
  onFinishSaleRequested() {
    if (
      this._saleManager.pendingPaymentsTotal.isGreaterThanOrEqualTo(this._saleManager.saleTotal)
    ) {
      window.merchantos.register.completeSale();
    }
  }

  _isPaymentTypeDisabledFromOptions({ paymentTypeID, paymentType, isDisabled = false }) {
    // if there are no set options or if another function sets the disabled state, is should be the go-to value
    if (!Object.keys(this.refundOptions).length || isDisabled) return isDisabled;

    // eslint-disable-next-line no-redeclare
    paymentType = paymentType || PaymentTypeManager.getPaymentType(paymentTypeID).get('type');

    if (this.refundOptions[PaymentType.Refunds.CREDIT_ACCOUNT_OR_GIFTCARD_ONLY]) {
      isDisabled = ![PaymentType.Types.CREDIT_ACCOUNT, PaymentType.Types.GIFT_CARD].includes(
        paymentType
      );
    }
    if (
      (this.refundOptions[PaymentType.Refunds.DISABLE_CREDIT_ACCOUNT_REFUNDS] &&
        paymentType === PaymentType.Types.CREDIT_ACCOUNT) ||
      (this.refundOptions[PaymentType.Refunds.DISABLE_GIFTCARD_REFUNDS] &&
        paymentType === PaymentType.Types.GIFT_CARD)
    ) {
      isDisabled = true;
    }

    return isDisabled;
  }

  /**
   * Updates the view's cash payment.
   * @private
   */
  _setViewPayment(paymentTypeID, amount, disable = false, hidden = false) {
    if (amount && !amount.isNaN()) {
      if (this._saleManager.isRefundSale()) {
        // for refunds we want the value reflected in the UI to be
        // positive
        amount = amount.abs();
      }

      // For invalid amounts such as characters, we do not update the payment, but we should show
      // to the user the reason for this behaviour
      this.view.setPayment(
        paymentTypeID,
        amount,
        this._isPaymentTypeDisabledFromOptions({ paymentTypeID, isDisabled: disable }),
        hidden
      );
    }
  }

  /**
   * Helper method for setInitialViewState()
   * Sets the Cash payment amount on the View
   *
   * @private
   */
  _setCashPayment() {
    let cashPaymentID = PaymentTypeManager.getPaymentIdForType(PaymentTypeManager.Types.CASH);
    let cashPaymentAmount = this._saleManager.getTotalPaymentsOfType(cashPaymentID);

    if (cashPaymentAmount.isZero()) {
      this._setViewPayment(cashPaymentID, new Decimal(0));
    } else {
      this._setViewPayment(cashPaymentID, new Decimal(cashPaymentAmount));
    }
  }

  /**
   * Helper method for setInitialViewState()
   * Sets the Credit Account payment amount on the View and
   * decides whether or not to disable that payment input
   * @private
   */
  _setCreditAccountPayment() {
    const creditAccountPaymentID = PaymentTypeManager.getPaymentIdForType(
      PaymentTypeManager.Types.CREDIT_ACCOUNT
    );
    const creditAccountAmount = this._saleManager.deposits.hasDeposits
      ? new Decimal(0)
      : this._saleManager.getTotalPaymentsOfType(creditAccountPaymentID);

    if (creditAccountPaymentID) {
      if (this._saleManager.creditAccountID) {
        this._setViewPayment(
          creditAccountPaymentID,
          creditAccountAmount,
          false,
          this._saleManager.deposits.hasDeposits
        );
      } else {
        this._setViewPayment(creditAccountPaymentID, new Decimal(0), true);
      }
    }
  }

  /**
   * Helper method for setInitialViewState()
   * Sets the User-defined payment amounts on the View
   *
   * @private
   */
  _setUserDefinedPayments() {
    let userDefinedPaymentTypes = PaymentTypeManager.getUserDefinedPaymentTypes();
    userDefinedPaymentTypes.forEach(paymentType => {
      let paymentTypeID = paymentType.get('paymentTypeID');
      let paymentAmount = this._saleManager.getTotalPaymentsOfType(paymentTypeID);

      if (paymentAmount.isZero()) {
        this._setViewPayment(paymentTypeID, new Decimal(0));
      } else {
        this._setViewPayment(paymentTypeID, new Decimal(paymentAmount));
      }
    });
  }

  /**
   * Updates the view's change due.
   * @private
   */
  _setViewChangeDue() {
    const cashPaymentTypeID = PaymentTypeManager.getPaymentIdForType(PaymentTypeManager.Types.CASH);
    const cashBalance = this._saleManager.getTotalPaymentsOfType(cashPaymentTypeID);

    if (cashBalance.isZero()) {
      this.view.showCashBalance(false);
    } else if (!this._saleManager.isRefundSale()) {
      this.view.setBalance(this._saleManager.pendingChange, cashBalance);
    }
  }

  /**
   * Determines the balance without any payments of the type specified.
   *
   * @param paymentTypeID {string|number} The ID of the payment type
   * @returns {Decimal}   The balance without any payments of the specified type
   * @private
   */
  _getBalanceWithoutPaymentOfType(paymentTypeID) {
    let totalOfCurrentPayments = this._saleManager.getTotalPaymentsOfType(paymentTypeID);
    let balance = this._saleManager.pendingBalance;

    if (!totalOfCurrentPayments.isZero()) {
      balance = balance.plus(totalOfCurrentPayments);
    }

    return balance;
  }

  /**
   * Gets the IDs of Payment Types that are Credit or Debit
   *
   * Helper method for _getRefundableCreditPayments() and
   * _getPaymentsRefundableByCredit()
   *
   * @returns {Array} - IDs of the Payment Types that are Credit or Debit
   * @private
   */
  _getCreditDebitPaymentTypeIDs() {
    let creditDebitPaymentTypes = new PaymentTypeCollection(
      PaymentTypeManager.allAvailableTypes.filter(paymentType => {
        let type = paymentType.get('type');

        return type === PaymentType.Types.CREDIT_CARD;
      })
    );

    let creditDebitPaymentTypeIDs = [];
    _.each(creditDebitPaymentTypes.models, creditDebitPaymentType => {
      creditDebitPaymentTypeIDs.push(creditDebitPaymentType.get('paymentTypeID'));
    });

    return creditDebitPaymentTypeIDs;
  }

  /**
   * Obtains a subset of refundable payments that were originally made
   * by credit/debit
   *
   * @returns {SalePaymentCollection} - Collection of refundable payments that are were
   *                    originally made by credit/debit
   * @private
   */
  _getRefundableCreditPayments() {
    let creditDebitPaymentTypeIDs = this._getCreditDebitPaymentTypeIDs();

    return this._saleManager.refundablePaymentList
      .filter(payment => {
        let paymentTypeID = payment.model.get('PaymentType').paymentTypeID;

        return _.contains(creditDebitPaymentTypeIDs, paymentTypeID);
      })
      .toSalePaymentCollection();
  }

  /**
   * Obtains a subset of refundable payments that are refundable
   * by credit/debit
   *
   * @returns {SalePaymentCollection} - Collection of refundable payments that are refundable by
   *                    credit/debit
   * @private
   */
  _getPaymentsRefundableByCredit() {
    let creditDebitPaymentTypeIDs = this._getCreditDebitPaymentTypeIDs();

    // Find the payments that are refundable by Credit/Debit (also
    // those refundable by any type (refundAsPaymentID 0)
    return this._saleManager.refundablePaymentList
      .filter(payment => {
        let refundAsPaymentID = payment.model.get('PaymentType').refundAsPaymentTypeID;

        return _.contains(creditDebitPaymentTypeIDs, refundAsPaymentID) || refundAsPaymentID === 0;
      })
      .toSalePaymentCollection();
  }

  /**
   * Determines whether or not the user can refund as anything but Credit Card
   * on the current transaction
   *
   * @returns {boolean} Whether or not the user can refund as anything but Credit Card
   * @private
   */
  _canOnlyRefundAsCredit() {
    let canOnlyRefundAsCredit = true;
    let creditDebitPaymentTypeIDs = this._getCreditDebitPaymentTypeIDs();

    this._saleManager.refundablePayments.forEach(refundablePayment => {
      let refundAsPaymentTypeID = refundablePayment.model.get('PaymentType').refundAsPaymentTypeID;
      if (!_.contains(creditDebitPaymentTypeIDs, refundAsPaymentTypeID)) {
        canOnlyRefundAsCredit = false;
      }
    });

    return canOnlyRefundAsCredit;
  }

  /**
   * Reset the UI amount disdplayed to the last accepted amount in the saleManager
   *
   * @param paymentTypeID {string|number} The ID of the Payment Type
   * @private
   */
  _resetViewToPreviousAmount(paymentTypeID) {
    let previousAmount = this._saleManager.getTotalPaymentsOfType(paymentTypeID);
    this._setViewPayment(paymentTypeID, previousAmount);
  }
}
